/**
 * REST client for the InboxGuard API.
 *
 * Replaces the legacy supabase-js calls everywhere. Handlers in `aws-rebuild/api/`
 * speak plain JSON; this client adds:
 *   - Authorization: Bearer <Cognito access token>, auto-refreshed via Amplify.
 *   - X-Request-ID for log correlation.
 *   - Typed error envelope handling.
 *   - 401 → trigger Amplify token refresh + one retry; if the refresh fails
 *     or the retry is still 401 (refresh token expired/revoked), sign out and
 *     hard-redirect to /auth?next=<current page> instead of error-walling
 *     every screen.
 *
 * Request signature is inspired by Supabase's `.from(...).select(...)` shape
 * but trimmed to what we actually use, so component diffs stay readable
 * during the migration.
 */
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

export class ApiHttpError extends Error implements ApiError {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiHttpError';
  }
}

interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** Force unauthenticated even if a session exists. Default: include token if available. */
  noAuth?: boolean;
  signal?: AbortSignal;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const search = new URLSearchParams();
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) search.set(k, String(v));
    }
  }
  const qs = search.size > 0 ? `?${search.toString()}` : '';
  const target = BASE.startsWith('http')
    ? new URL(path.replace(/^\//, ''), BASE.endsWith('/') ? BASE : BASE + '/').toString() + qs
    : `${BASE.replace(/\/$/, '')}/${path.replace(/^\//, '')}${qs}`;

  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'x-request-id': crypto.randomUUID(),
  };

  if (!opts.noAuth) {
    const token = await getAccessToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(target, {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // 401 once → try to refresh and retry. If the refresh itself fails or the
  // retry still 401s, the refresh token is dead — send the user back to
  // sign-in instead of throwing at every caller.
  if (res.status === 401 && !opts.noAuth) {
    const refreshed = await getAccessToken({ forceRefresh: true });
    if (!refreshed) return handleSessionExpired();
    headers.authorization = `Bearer ${refreshed}`;
    const retry = await fetch(target, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
    if (retry.status === 401) return handleSessionExpired();
    return parseResponse<T>(retry);
  }

  return parseResponse<T>(res);
}

// Set once we've kicked off a sign-out + redirect so concurrent in-flight
// requests (TanStack fires several per page) don't stack multiple redirects.
let sessionExpiredRedirectStarted = false;

/**
 * The refresh token is expired or revoked. Sign out via Amplify (clears the
 * stale local session) and hard-redirect to /auth with a `next` param so the
 * user lands back where they were after re-authenticating. Never redirects
 * while already on /auth (avoids loops) and at most once per page load.
 * Always throws so callers' error paths still settle.
 */
async function handleSessionExpired(): Promise<never> {
  const onAuthPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/auth');
  if (!sessionExpiredRedirectStarted && !onAuthPage && typeof window !== 'undefined') {
    sessionExpiredRedirectStarted = true;
    try {
      await signOut();
    } catch {
      // Best effort — the redirect to /auth is the real recovery path.
    }
    const next = window.location.pathname + window.location.search;
    window.location.assign(`/auth?next=${encodeURIComponent(next)}`);
  }
  throw new ApiHttpError(
    401,
    'SESSION_EXPIRED',
    'Your session has expired. Please sign in again.',
  );
}

async function parseResponse<T>(res: Response): Promise<T> {
  let json: unknown;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  if (!res.ok) {
    // Tolerate both error body shapes the API emits:
    //   { error: { code, message, details?, requestId? } }  — standard envelope
    //   { error: 'some_code', message: '…' }                — kill-switch /
    //     gateway-style responses (e.g. scan-domain's 503)
    // Never lean on res.statusText for the message — it's empty over HTTP/2.
    const body = json as { error?: unknown; message?: unknown } | null;
    let code = `HTTP_${res.status}`;
    let message: string | undefined;
    let details: unknown;
    let requestId: string | undefined;
    if (body?.error && typeof body.error === 'object') {
      const env = body.error as Partial<ApiError>;
      if (typeof env.code === 'string') code = env.code;
      if (typeof env.message === 'string') message = env.message;
      details = env.details;
      requestId = env.requestId;
    } else if (typeof body?.error === 'string') {
      code = body.error;
      message = typeof body.message === 'string' ? body.message : body.error;
    } else if (typeof body?.message === 'string') {
      message = body.message;
    }
    const finalMessage = message && message.trim() ? message : `HTTP ${res.status}`;
    throw new ApiHttpError(res.status, code, finalMessage, details, requestId);
  }
  return json as T;
}

async function getAccessToken(opts: { forceRefresh?: boolean } = {}): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: opts.forceRefresh });
    // ID token, not access token — custom:org_id / custom:app_role are only
    // emitted on the ID token by Cognito. The backend authorizer is keyed to
    // the same audience, so this is the right token to send.
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Authenticated GET that returns the raw response Blob (e.g. a PDF). The normal
 * `request` path JSON-parses every response, so binary downloads use this.
 * Browser navigation can't carry the bearer token, so we fetch + blob in JS.
 */
async function requestBlob(path: string): Promise<Blob> {
  const target = BASE.startsWith('http')
    ? new URL(path.replace(/^\//, ''), BASE.endsWith('/') ? BASE : BASE + '/').toString()
    : `${BASE.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  const headers: Record<string, string> = { 'x-request-id': crypto.randomUUID() };
  const token = await getAccessToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(target, { headers });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: { message?: string } };
      if (j?.error?.message) message = j.error.message;
    } catch {
      /* binary/empty error body — keep the status message */
    }
    throw new ApiHttpError(res.status, `HTTP_${res.status}`, message);
  }
  return res.blob();
}

// Public API — what the rest of the app imports.
export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
};
